import type { fleetState } from '@kanbios/api-client';

/** D46: what a container's image runs, read by Trivy on the server. */
export type ContainerTechnologies = fleetState.components['schemas']['ContainerTechnologies'];
export type Technology = fleetState.components['schemas']['Technology'];
type Counts = NonNullable<ContainerTechnologies['vulnerabilities']>;

/**
 * The first `parts` numbers of a version: « 20.11.1 » → « 20.11 » (2), « 14 » (1).
 * A 0.x version changes with its second number: « 0.115.0 » → « 0.115 » (1).
 */
export function shortVersion(version: string | null, parts: number): string | null {
  const numbers = version?.match(/^\d+(?:\.\d+)*/)?.[0];
  return numbers ? numbers.split('.').slice(0, numbers.startsWith('0.') ? parts + 1 : parts).join('.') : version;
}

/** « Node 20.11.1 », or shortened (« Node 20.11 ») when `parts` is given. */
export const named = (x: Technology, parts?: number) => [x.label, parts ? shortVersion(x.version, parts) : x.version].filter(Boolean).join(' ');

/**
 * One line for a table row: the main runtime (major.minor) and the first
 * framework (major), « Node 20.11 · Next.js 14 »; the others are counted.
 * D60: without a runtime, the main service in its place (« PostgreSQL 16.4 »).
 */
export function technologiesLine(x: ContainerTechnologies): { text: string; others: Technology[] } {
  const [runtime, ...runtimes] = x.runtimes;
  const [service, ...services] = runtime ? [] : x.services;
  const [framework, ...frameworks] = x.frameworks;
  const main = runtime ?? service;
  return {
    text: [main && named(main, 2), framework && named(framework, 1)].filter(Boolean).join(' · '),
    others: [...runtimes, ...(runtime ? x.services : services), ...frameworks],
  };
}

/** Everything, full versions: what the column searches and sorts on. */
export const technologiesText = (x: ContainerTechnologies | null) => (x ? [...x.runtimes, ...x.services, ...x.frameworks].map(t => named(t)).join(' · ') : '');

/**
 * D47, D60: a runtime or a service past its end of life is a defect of the
 * application (red); a framework is information (orange).
 */
export const endedOf = (x: ContainerTechnologies | null) => (x ? [...x.runtimes, ...x.services, ...x.frameworks].filter(e => e.ended) : []);
export const endedIsDefect = (x: ContainerTechnologies | null) => Boolean(x && [...x.runtimes, ...x.services].some(r => r.ended));

/** D61: how far behind its latest release a technology is: a badge's text and tone, null when the latest is unknown. Information only. */
export function behindState(x: Technology): { label: string; tone: 'warn' | 'neutral' | 'pos'; sort: number } | null {
  if (!x.latest || !x.version) return null;
  if (!x.behind) return { label: 'technologies.upToDate', tone: 'pos', sort: 0 };
  const rank = { patch: 1, minor: 2, major: 3 }[x.behind.kind];
  return { label: `technologies.behind.${x.behind.kind}`, tone: x.behind.kind === 'major' ? 'warn' : 'neutral', sort: rank * 10_000 + x.behind.count };
}

export const vulnerabilityTotal = (v: Counts | null) => (v ? v.critical + v.high + v.medium + v.low + v.unknown : 0);

/** Critical ones are red, high ones orange, the rest neutral: information only (lot 3 ranks by real risk). */
export const vulnerabilityTone = (v: Counts | null): 'neg' | 'warn' | 'neutral' => (!v ? 'neutral' : v.critical > 0 ? 'neg' : v.high > 0 ? 'warn' : 'neutral');

/** D56: one known vulnerability of the image. */
export type Cve = fleetState.components['schemas']['Cve'];

/** D56: the gravity as SPEC-018 shows it: critical red, high orange, the others neutral. */
export const severityTone = (severity: Cve['severity']): 'neg' | 'warn' | 'neutral' => (severity === 'critical' ? 'neg' : severity === 'high' ? 'warn' : 'neutral');

/** D56: a CVE's page on Aqua's vulnerability database (Trivy's source); none for the other identifiers (GHSA…). */
export const cveLink = (id: string): string | null => (/^CVE-\d{4}-\d+$/i.test(id) ? `https://avd.aquasec.com/nvd/${id.toLowerCase()}` : null);
